// Copyright (C) 2017-2023 Smart code 203358507

const React = require('react');
const { useTranslation } = require('react-i18next');
const { useLocation, useParams, useNavigate } = require('react-router');
const { useSearchParams } = require('react-router-dom');
const PropTypes = require('prop-types');
const classnames = require('classnames');
const NotFound = require('stremio/routes/NotFound');
const { useNotifications, useOnScrollToBottom, useModelState, withCoreSuspender } = require('stremio/common');
const { default: toPath } = require('stremio-router/toPath');
const { DelayedRenderer, Chips, Image, MainNavBars, LibItem, MultiselectMenu } = require('stremio/components');
const { default: Placeholder } = require('./Placeholder');
const useLibrary = require('./useLibrary');
const useSelectableInputs = require('./useSelectableInputs');
const useCarolineContentAccess = require('../../caroline/auth/useCarolineContentAccess');
const useCarolineLibrary = require('../../caroline/library/useCarolineLibrary').default;
const CarolineLibraryItem = require('./CarolineLibraryItem');
const { libraryKey } = require('../../caroline/library/libraryKey');
const {
    METADATA_CONCURRENCY,
    SUPPORTED_LIBRARY_NAME_SORTS,
    createMetadataQueueState,
    metadataCacheGet,
    metadataCacheSet,
    settleMetadataQueueKey,
    sortCarolineReferences,
    toCarolineMetadata,
    toCarolineLibraryViewModels,
} = require('../../caroline/library/metadata');
const styles = require('./styles');

const SCROLL_TO_BOTTOM_TRESHOLD = 400;
const CAROLINE_LIBRARY_SORTS = SUPPORTED_LIBRARY_NAME_SORTS;

function withModel(Library) {
    const LibraryWithModel = () => {
        const location = useLocation();
        const model = React.useMemo(() => {
            return typeof location.pathname === 'string' ?
                location.pathname.match('/library') ?
                    'library'
                    :
                    location.pathname.match('/continuewatching') ?
                        'continue_watching'
                        :
                        null
                :
                null;
        }, [location?.pathname]);

        if (model === null) return <NotFound />;

        return <Library model={model} />;
    };
    LibraryWithModel.displayName = 'LibraryWithModel';
    return LibraryWithModel;
}

const ContinueWatching = () => {
    const model = 'continue_watching';
    const { type } = useParams();
    const urlParams = React.useMemo(() => ({ type }), [type]);
    const [queryParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const canAccessCarolineContent = useCarolineContentAccess();
    const notifications = useNotifications();
    const [library, loadNextPage] = useLibrary(model, urlParams, queryParams);
    const [typeSelect, sortChips, hasNextPage] = useSelectableInputs(library);
    const scrollContainerRef = React.useRef(null);
    const onScrollToBottom = React.useCallback(() => {
        if (hasNextPage) loadNextPage();
    }, [hasNextPage, loadNextPage]);
    const onScroll = useOnScrollToBottom(onScrollToBottom, SCROLL_TO_BOTTOM_TRESHOLD);
    React.useLayoutEffect(() => {
        if (scrollContainerRef.current !== null && library.selected && library.selected.request.page === 1 && library.catalog.length !== 0) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [canAccessCarolineContent, library.selected, library.catalog.length]);
    React.useEffect(() => {
        if (!library.selected?.type && typeSelect.value) {
            navigate(toPath(typeSelect.value));
        }
    }, [typeSelect.value, library.selected, navigate]);

    if (canAccessCarolineContent.status === 'loading') {
        return <MainNavBars className={styles['library-container']} route={model} />;
    }

    return (
        <MainNavBars className={styles['library-container']} route={model}>
            {canAccessCarolineContent.canAccess ? (
                <div className={styles['library-content']}>
                    <div className={styles['selectable-inputs-container']}>
                        <MultiselectMenu {...typeSelect} className={styles['select-input-container']} />
                        <Chips {...sortChips} className={styles['select-input-container']} />
                    </div>
                    {library.selected === null ? (
                        <DelayedRenderer delay={500}>
                            <div className={styles['message-container']}>
                                <Image className={styles['image']} src={require('/assets/images/empty.png')} alt={' '} />
                                <div className={styles['message-label']}>{t('BOARD_CONTINUE_WATCHING_NOT_LOADED')}</div>
                            </div>
                        </DelayedRenderer>
                    ) : library.catalog.length === 0 ? (
                        <div className={styles['message-container']}>
                            <Image className={styles['image']} src={require('/assets/images/empty.png')} alt={' '} />
                            <div className={styles['message-label']}>{t('BOARD_CONTINUE_WATCHING_EMPTY')}</div>
                        </div>
                    ) : (
                        <div ref={scrollContainerRef} className={classnames(styles['meta-items-container'], 'animation-fade-in')} onScroll={onScroll}>
                            {library.catalog.map((libItem, index) => (
                                <LibItem {...libItem} key={index} notifications={notifications} removable={false} detailsVideosFirst={false} />
                            ))}
                        </div>
                    )}
                </div>
            ) : <Placeholder unavailable={canAccessCarolineContent.status === 'unavailable'} />}
        </MainNavBars>
    );
};

const CarolineLibrary = () => {
    const { type } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [queryParams, setQueryParams] = useSearchParams();
    const contentAccess = useCarolineContentAccess();
    const library = useCarolineLibrary();
    const [metadataQueue, setMetadataQueue] = React.useState(() => createMetadataQueueState([]));
    const [metadataRevision, setMetadataRevision] = React.useState(0);
    const scrollContainerRef = React.useRef(null);
    const allowedSort = queryParams.get('sort');
    const sort = CAROLINE_LIBRARY_SORTS.includes(allowedSort) ? allowedSort : null;

    React.useEffect(() => {
        if (allowedSort !== null && !CAROLINE_LIBRARY_SORTS.includes(allowedSort)) {
            setQueryParams((current) => {
                const next = new URLSearchParams(current);
                next.delete('sort');
                return next;
            }, { replace: true });
        }
    }, [allowedSort, setQueryParams]);

    const mediaTypes = React.useMemo(() => Array.from(new Set(library.items.map(({ mediaType }) => mediaType))).sort(), [library.items]);
    const references = React.useMemo(
        () => {
            const filtered = type ? library.items.filter((item) => item.mediaType === type) : library.items;
            return sortCarolineReferences(filtered, (reference) => metadataCacheGet(reference.mediaType, reference.metaId)?.metadata, sort);
        },
        // metadataRevision invalidates these reads from the module-level metadata cache.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- the cache has no React identity.
        [library.items, type, sort, metadataRevision],
    );
    const viewModels = React.useMemo(
        () => toCarolineLibraryViewModels(references),
        // metadataRevision invalidates these reads from the module-level metadata cache.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- the cache has no React identity.
        [references, metadataRevision],
    );
    const metadataKeys = React.useMemo(() => references.map(({ mediaType, metaId }) => libraryKey(mediaType, metaId)), [references]);
    const metadataKeysKey = JSON.stringify(metadataKeys);
    const activeMetadataKey = Array.from(metadataQueue.active)[0];
    const activeMetadataReference = activeMetadataKey ? references.find(({ mediaType, metaId }) => libraryKey(mediaType, metaId) === activeMetadataKey) : null;
    const activeMetadataMediaType = activeMetadataReference?.mediaType;
    const activeMetadataMetaId = activeMetadataReference?.metaId;
    const metadataAction = React.useMemo(() => activeMetadataMediaType && activeMetadataMetaId ? ({
        action: 'Load',
        args: {
            model: 'MetaDetails',
            args: {
                metaPath: { resource: 'meta', type: activeMetadataMediaType, id: activeMetadataMetaId, extra: [] },
                streamPath: null,
                guessStream: false,
            },
        },
    }) : null, [activeMetadataMediaType, activeMetadataMetaId]);
    const metadataState = useModelState({ model: 'meta_details', action: metadataAction });
    const loadedMetaPath = metadataState?.selected?.metaPath;
    const activeMetadataMatches = activeMetadataReference !== null
        && loadedMetaPath?.resource === 'meta'
        && loadedMetaPath.type === activeMetadataReference.mediaType
        && loadedMetaPath.id === activeMetadataReference.metaId;
    const metadataLoadable = activeMetadataMatches ? metadataState.metaItem?.content : null;

    React.useEffect(() => {
        const cachedKeys = metadataKeys.filter((key, index) => metadataCacheGet(references[index].mediaType, references[index].metaId) !== undefined);
        setMetadataQueue((previous) => {
            const reconciled = createMetadataQueueState(metadataKeys, METADATA_CONCURRENCY, cachedKeys);
            for (const key of previous.settled) {
                if (metadataKeys.includes(key)) reconciled.settled.add(key);
            }
            for (const key of previous.active) {
                if (metadataKeys.includes(key) && !reconciled.settled.has(key) && reconciled.active.size < METADATA_CONCURRENCY) reconciled.active.add(key);
            }
            return reconciled;
        });
    }, [metadataKeysKey, metadataKeys, references]);

    React.useEffect(() => {
        if (!activeMetadataReference || !activeMetadataMatches) return;
        if (metadataLoadable?.type === 'Ready') {
            const metadata = toCarolineMetadata(metadataLoadable.content);
            if (metadata && metadata.type === activeMetadataReference.mediaType && metadata.id === activeMetadataReference.metaId) {
                metadataCacheSet(activeMetadataReference.mediaType, activeMetadataReference.metaId, { status: 'ready', metadata });
            } else {
                metadataCacheSet(activeMetadataReference.mediaType, activeMetadataReference.metaId, { status: 'unavailable', metadata: null });
            }
            setMetadataQueue((previous) => settleMetadataQueueKey(previous, activeMetadataKey, metadataKeys, METADATA_CONCURRENCY));
            setMetadataRevision((revision) => revision + 1);
        } else if (metadataLoadable?.type === 'Err') {
            metadataCacheSet(activeMetadataReference.mediaType, activeMetadataReference.metaId, { status: 'unavailable', metadata: null });
            setMetadataQueue((previous) => settleMetadataQueueKey(previous, activeMetadataKey, metadataKeys, METADATA_CONCURRENCY));
            setMetadataRevision((revision) => revision + 1);
        }
    }, [activeMetadataReference, activeMetadataMatches, activeMetadataKey, metadataKeys, metadataLoadable]);

    const typeSelect = React.useMemo(() => ({
        options: [
            { value: '', label: t('TYPE_ALL') },
            ...mediaTypes.map((mediaType) => ({
                value: mediaType,
                label: t(`TYPE_${mediaType.toUpperCase()}`, { defaultValue: mediaType }),
            })),
        ],
        value: type || '',
        onSelect: (value) => navigate(value ? `/library/${encodeURIComponent(value)}` : '/library'),
    }), [mediaTypes, type, navigate, t]);
    const sortChips = React.useMemo(() => ({
        options: CAROLINE_LIBRARY_SORTS.map((sortValue) => ({
            value: sortValue,
            label: t(`SORT_${sortValue.toUpperCase()}`),
        })),
        selected: sort ? [sort] : [],
        onSelect: (value) => setQueryParams((current) => {
            const next = new URLSearchParams(current);
            if (next.get('sort') === value) next.delete('sort');
            else next.set('sort', value);
            return next;
        }),
    }), [sort, setQueryParams, t]);

    React.useLayoutEffect(() => {
        if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
    }, [type, sort]);

    if (contentAccess.status === 'loading' || (contentAccess.canAccess && (library.status === 'loading' || library.status === 'idle'))) {
        return <MainNavBars className={styles['library-container']} route={'library'} />;
    }

    return (
        <MainNavBars className={styles['library-container']} route={'library'}>
            {contentAccess.canAccess ? (
                <div className={styles['library-content']}>
                    <div className={styles['selectable-inputs-container']}>
                        <MultiselectMenu {...typeSelect} className={styles['select-input-container']} />
                        <Chips {...sortChips} className={styles['select-input-container']} />
                    </div>
                    {library.status === 'unavailable' || (library.status === 'error' && library.items.length === 0) ? (
                        <div className={styles['message-container']} role={'status'}>
                            <Image className={styles['image']} src={require('/assets/images/empty.png')} alt={' '} />
                            <div className={styles['message-label']}>{t('CAROLINE_LIBRARY_UNAVAILABLE', { defaultValue: 'Caroline Library is temporarily unavailable.' })}</div>
                        </div>
                    ) : references.length === 0 ? (
                        <div className={styles['message-container']}>
                            <Image className={styles['image']} src={require('/assets/images/empty.png')} alt={' '} />
                            <div className={styles['message-label']}>{t('LIBRARY_EMPTY')}</div>
                        </div>
                    ) : (
                        <div ref={scrollContainerRef} className={classnames(styles['meta-items-container'], 'animation-fade-in')}>
                            {viewModels.map(({ reference, metadataStatus }) => {
                                const key = libraryKey(reference.mediaType, reference.metaId);
                                const cached = metadataCacheGet(reference.mediaType, reference.metaId);
                                return (
                                    <CarolineLibraryItem
                                        key={key}
                                        reference={reference}
                                        metadataStatus={metadataStatus}
                                        metadata={cached?.metadata || null}
                                        removeItem={library.remove}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
            ) : <Placeholder unavailable={contentAccess.status === 'unavailable'} />}
        </MainNavBars>
    );
};

const Library = ({ model }) => model === 'library' ? <CarolineLibrary /> : <ContinueWatching />;

Library.propTypes = {
    model: PropTypes.oneOf(['library', 'continue_watching']),
};

const LibraryFallback = ({ model }) => <MainNavBars className={styles['library-container']} route={model} />;
LibraryFallback.propTypes = Library.propTypes;

module.exports = withModel(withCoreSuspender(Library, LibraryFallback));
